import { useCallback, useState } from 'react'
import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import _ from 'lodash'
import {
  RestoreType,
  useBulkSheetState as useBulkSheetStateBase,
} from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'

// ag-grid の getFilterModel/setFilterModel が扱うフィルタ表現。ライブラリから個別の型 export がないため
// ローカルで Record として定義する。各値の中身はフィルタ種別に依存するので unknown で扱う。
type FilterModel = Record<string, unknown>

// 配列リテラルを useBulkSheetStateBase に直接渡すと毎レンダー新しい参照になり、
// 内部の useMemo / useCallback / useEffect が連鎖発火して uistate API が無限にコールされる。
// モジュールスコープの定数で参照を固定する。
const RESTORE_TYPES: RestoreType[] = ['COLUMN_STATE', 'FILTER_STATE']

// 派生 state の計算: フィルタモデルから FilterButton 用の ColDef リストを引く
const toFilteredColumns = (
  gridApi: GridApi,
  filterModel: FilterModel
): ColDef[] =>
  Object.keys(filterModel)
    .map(colKey => gridApi.getColumnDef(colKey))
    .filter((col): col is ColDef => !!col)

// 派生 state の計算: columnState から SortButton 用のソート列リストを組み立てる
const toSortedColumnState = (
  gridApi: GridApi,
  columnState: ColumnState[]
): SortedColumnState[] =>
  columnState
    .filter(col => !!col.sort)
    .map(col => {
      const def = gridApi.getColumnDef(col.colId)
      return {
        colId: col.colId,
        field: def?.field,
        headerName: def?.headerName,
        sort: col.sort,
      }
    })

// ag-grid の列状態 / フィルタ状態を SprintReport 用キーで永続化する。
// 旧 BulkSheet クラス版で SprintReportColumnAndFilterState-<projectUuid> に保存していたものに合わせる。
export const useSprintReportBulkSheetState = (
  gridApi: GridApi | null,
  functionUuid: string,
  projectUuid: string
) => {
  // 第2引数 key は uistate キーのサフィックス（"<uiStateKey>-<key>" になる）。
  // 第5引数 projectUuid は permanent-saved-UI-state の URL 復元 (?arg=...) のために
  // ストレージサービスへ別途渡される。役割は違うが SprintReport ではどちらもプロジェクトUUIDで識別するため同値で良い。
  const base = useBulkSheetStateBase(
    functionUuid,
    projectUuid,
    UiStateKey.SprintReportColumnAndFilterState,
    RESTORE_TYPES,
    projectUuid
  )

  // FilterButton / SortButton 表示用のフィルタ・ソート対象列を保持する。
  // ag-grid の filterModel / columnState から派生するが、ColDef を持ち回るために別途 state で管理する。
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  const restoreColumnState = useCallback(() => {
    if (!gridApi || _.isEmpty(base.columnState)) return
    gridApi.applyColumnState({
      state: base.columnState,
      applyOrder: true,
    })
  }, [base.columnState, gridApi])

  // 復元完了直後に保存済みフィルタ / ソート状態から FilterButton / SortButton 用の派生 state を組み立てる。
  const syncDerivedStateFromRestored = useCallback(() => {
    if (!gridApi) return
    if (!_.isEmpty(base.filterState)) {
      setFilteredColumns(toFilteredColumns(gridApi, base.filterState ?? {}))
    }
    setSortColumnsState(toSortedColumnState(gridApi, base.columnState ?? []))
  }, [gridApi, base.columnState, base.filterState])

  const onFirstDataRendered = useCallback(
    (_e: FirstDataRenderedEvent) => {
      restoreColumnState()
      if (!_.isEmpty(base.filterState)) {
        gridApi?.setFilterModel(base.filterState ?? null)
      }
      syncDerivedStateFromRestored()
      base.finishRestoring()
    },
    [base, gridApi, restoreColumnState, syncDerivedStateFromRestored]
  )

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi?.getColumnState()
    columnState && base.saveColumnState(columnState)
  }, [base, gridApi])

  const refreshSortColumnsState = useCallback(() => {
    if (!gridApi) return
    setSortColumnsState(toSortedColumnState(gridApi, gridApi.getColumnState()))
  }, [gridApi])

  const onSortChanged = useCallback(
    (_e: SortChangedEvent) => {
      rememberColumnState()
      refreshSortColumnsState()
    },
    [rememberColumnState, refreshSortColumnsState]
  )

  const onColumnMoved = useCallback(
    (_e: ColumnMovedEvent) => {
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onColumnResized = useCallback(
    (e: ColumnResizedEvent) => {
      // ドラッグ中は finished=false で連続発火するので、終端のみ保存
      if (!e.finished) return
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onColumnVisible = useCallback(
    (_e: ColumnVisibleEvent) => {
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api.getFilterModel()
      base.saveFilterState(filterModel)
      setFilteredColumns(toFilteredColumns(e.api, filterModel))
    },
    [base]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!gridApi) return
      const colKey = column.colId || column.field || ''
      // ag-grid 内部表現を mutate しないよう、削除対象キーを除いた新しいオブジェクトを作って渡す
      const { [colKey]: _omit, ...rest } = gridApi.getFilterModel()
      gridApi.setFilterModel(rest)
    },
    [gridApi]
  )

  const resetFilters = useCallback(() => {
    if (!gridApi) return
    gridApi.setFilterModel(null)
  }, [gridApi])

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<any>) => {
      if (!gridApi) return
      const colIdStr = colId.toString()
      gridApi.applyColumnState({
        state: [{ colId: colIdStr, sort: null }],
      })
    },
    [gridApi]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridApi) return
    gridApi.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridApi])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridApi) return
      gridApi.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridApi]
  )

  // 列設定ポッパーの「列設定の初期化」操作後に派生 state を再同期する。
  // gridApi.resetColumnState() などは ag-grid のイベントを発火させないので、明示的に setState する。
  const resetDerivedState = useCallback(() => {
    setFilteredColumns([])
    setSortColumnsState([])
  }, [])

  // 保存済み UI state（列状態 + フィルタ状態）を現在のグリッドに適用する。
  // grid イベント経由でも派生 state は更新されるが、setFilterModel の非同期挙動や
  // 復元タイミングのずれを避けるため、明示的に同期させる。
  const applyUIState = useCallback(
    (uiState: { columnState?: ColumnState[]; filterState?: FilterModel }) => {
      if (!gridApi) return
      const columnState = uiState.columnState
      const filterState = uiState.filterState
      if (columnState && !_.isEmpty(columnState)) {
        gridApi.applyColumnState({ state: columnState, applyOrder: true })
        base.saveColumnState(columnState)
        setSortColumnsState(toSortedColumnState(gridApi, columnState))
      }
      if (filterState !== undefined) {
        // null/undefined のばらつきを 1 度に解消してから各副作用に渡す
        const next: FilterModel = filterState ?? {}
        gridApi.setFilterModel(next)
        base.saveFilterState(next)
        setFilteredColumns(toFilteredColumns(gridApi, next))
      }
    },
    [base, gridApi]
  )

  return {
    onFirstDataRendered,
    onSortChanged,
    onColumnMoved,
    onColumnResized,
    onColumnVisible,
    onFilterChanged,
    filteredColumns,
    sortColumnsState,
    onDeleteFilteredColumn,
    resetFilters,
    onDeleteSortedColumn,
    onDeleteSortedAllColumns,
    onChangeSortColumnState,
    columnState: base.columnState,
    filterState: base.filterState,
    saveColumnState: base.saveColumnState,
    saveFilterState: base.saveFilterState,
    resetDerivedState,
    applyUIState,
  }
}
